import { yupResolver } from '@hookform/resolvers/yup'
import { useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ServiceFormFields } from '../serviceForm/ServiceFormFields'
import { emptyServiceForm, serviceFormSchema, toCreateProps, type ServiceFormValues } from '../serviceForm/serviceForm'
import { getServiceErrorKey, isNameTaken } from '../serviceErrors'
import { useConfirmationResult } from '../useConfirmationResult'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation } from '@percent/utility'
import { ConfirmationResultModal } from '@percent/lemonade'

type CreateServiceModalProps = {
  onClose: () => void
  onCreated: () => void
}

export function CreateServiceModal({ onClose, onCreated }: Readonly<CreateServiceModalProps>) {
  const { t } = useTranslation()
  const { internalServiceService } = useServices()
  const [{ isPending }, { mutateAsync: createService }] = useMutation(internalServiceService.createService)
  const result = useConfirmationResult({ onClose, onSuccess: onCreated, isBusy: isPending })
  const schema = useMemo(() => serviceFormSchema(t), [t])
  const form = useForm<ServiceFormValues>({
    defaultValues: emptyServiceForm,
    mode: 'onChange',
    resolver: yupResolver(schema)
  })
  const name = useWatch({ control: form.control, name: 'name' }).trim()

  const submit = form.handleSubmit(async values => {
    try {
      await createService(toCreateProps(values))
      result.succeed()
    } catch (error) {
      if (isNameTaken(error)) {
        form.setError('name', { message: t('internalServices.error.nameTaken') })
      } else {
        result.fail(error)
      }
    }
  })

  return (
    <ConfirmationResultModal
      isOpen
      state={result.state}
      title={t('modal.createService.title')}
      primaryButtonText={t('button.createService')}
      loadingText={t('modal.createService.button.creating')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={submit}
      onClose={result.dismiss}
      onFailureClose={result.backToConfirm}
      disabled={!form.formState.isValid || isPending}
      loading={isPending}
      primaryBtnTestId="create-service-submit-button"
      secondaryBtnTestId="create-service-cancel-button"
      buttonTestId="create-service-result-button"
      success={{
        title: t('modal.createService.success.title'),
        description: t('modal.createService.success.description', { name }),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.createService.error.title'),
        description: t(getServiceErrorKey(result.error) ?? 'modal.createService.error.description', { name }),
        button: t('button.close')
      }}
    >
      <ServiceFormFields form={form} dataTestId="create-service" />
    </ConfirmationResultModal>
  )
}
